import type React from "react";
import { useId } from "react";
import Switch from "../../primitives/Switch";
import { FieldMessages, fieldAccessibility, type LegacyField } from "./LegacyFieldAdapter";

// Redux Form adapter for an on/off switch, extracted from inputs.jsx as
// `ToggleField` as part of replacing react-toggle. inputs.jsx re-exports it under
// the old name, so existing call sites are unchanged.
//
// The adapter owns every Redux Form concern: reading field.input, translating the
// primitive's boolean callback back into field.input.onChange, and rendering the
// label and messages. The primitive underneath knows none of that.

type SwitchFieldProps = LegacyField & {
  /**
   * Passed by every call site and deliberately not forwarded. It is
   * `app-form-control`, a text-input skin; applying it to the switch would
   * repaint the control. `ToggleField` ignored it too.
   */
  className?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  id?: string;
  input: {
    name?: string;
    onBlur?: (value?: boolean | string) => void;
    onChange: (value: boolean) => void;
    value?: boolean | string;
  };
  onInputChange?: (value: boolean) => void;
  placeholder?: React.ReactNode;
};

function hiddenLabel(field: SwitchFieldProps): string | undefined {
  if (!field.hideLabel) {
    return undefined;
  }
  if (typeof field.placeholder === "string" && field.placeholder.trim()) {
    return field.placeholder;
  }
  const name = field.input.name?.replace(/[_-]+/g, " ").trim();
  return name ? `${name[0].toUpperCase()}${name.slice(1)}` : "Switch";
}

export default function SwitchField(field: SwitchFieldProps): React.ReactElement {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);

  // Previously `defaultChecked={!!field.input.value}`, which left the control
  // uncontrolled: once rendered it stopped tracking the store, so a reinitialised
  // or programmatically changed form showed a stale position. The checked state
  // is now computed from the store on every render and passed explicitly.
  const checked = Boolean(field.input.value);

  return (
    <div className={accessibility.hasError ? "app-validation-error" : undefined}>
      {/* oxlint-disable-next-line jsx-a11y/label-has-associated-control -- the owned Switch renders the native input this label associates with. */}
      <label className="toggle-label">
        <Switch
          aria-describedby={accessibility.inputProps["aria-describedby"]}
          aria-invalid={accessibility.inputProps["aria-invalid"]}
          aria-required={accessibility.inputProps["aria-required"]}
          checked={checked}
          disabled={field.disabled}
          // ToggleField never passed the replaced library's `icons` prop, so its
          // default applied and these nine Redux Form switches have always drawn
          // the check and cross glyphs. The owned Switch defaults the other way,
          // matching the fifty-three call sites that opted out explicitly, so the
          // adapter has to ask for them.
          icons
          id={inputId}
          label={hiddenLabel(field)}
          // Mark touched without replaying the boolean captured before an
          // immediately preceding switch change committed to Redux Form.
          onBlur={field.input.onBlur ? () => field.input.onBlur?.() : undefined}
          onCheckedChange={(next) => {
            field.input.onChange(next);
            // Deferred exactly as before: several call sites submit the form from
            // this callback and depend on the store having settled first.
            if (field.onInputChange) {
              setImmediate(() => field.onInputChange?.(next));
            }
          }}
        />
        <span>{field.hideLabel ? null : field.placeholder}</span>
      </label>
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}
